import { useEffect } from 'react';
import { notification } from 'antd';
import { connectSocket, getSocket } from '../services/socket';
import { showBrowserNotification } from '../utils/browserNotify';

export interface HandoverEventData {
  requestId?:       string;
  requestedByName?: string;
  amount?:          number;
  message?:         string;
  createdAt?:       string;
  respondedAt?:     string;
}

// Connects the socket for handover-request notifications as soon as a logged-in
// user is on hand. Incoming requests are handed to `onRequested` (the caller
// shows the centered accept/reject popup for those); accepted/rejected are
// just informational, so those still get a plain toast here.
const useHandoverSocket = (onRequested?: (data: HandoverEventData) => void) => {
  useEffect(() => {
    let userId: string | undefined;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      userId = user?._id || user?.id;
    } catch {
      /* ignore */
    }
    if (!userId) return;

    connectSocket(userId);
    const socket = getSocket();
    if (!socket) return;

    const handleRequested = (data: HandoverEventData) => {
      const body = data?.message || 'A new handover request was raised.';
      if (onRequested) onRequested(data);
      else notification.info({ message: 'Handover Request', description: body });
      showBrowserNotification('Handover Request', body);
    };
    const onAccepted = (data: HandoverEventData) => {
      const body = data?.message || 'Your handover request was accepted.';
      notification.success({ message: 'Handover Accepted', description: body });
      showBrowserNotification('Handover Accepted', body);
    };
    const onRejected = (data: HandoverEventData) => {
      const body = data?.message || 'Your handover request was rejected.';
      notification.error({ message: 'Handover Rejected', description: body });
      showBrowserNotification('Handover Rejected', body);
    };

    socket.on('handover_requested', handleRequested);
    socket.on('handover_accepted', onAccepted);
    socket.on('handover_rejected', onRejected);

    return () => {
      socket.off('handover_requested', handleRequested);
      socket.off('handover_accepted', onAccepted);
      socket.off('handover_rejected', onRejected);
    };
  }, [onRequested]);
};

export default useHandoverSocket;
